import React, { useState } from "react";
import { PIPELINE_STAGE_LABELS } from "../services/jobService";

// Resume Screening is always first, Final Ranking is always last — every
// other stage is optional and reorderable in between. Native HTML5 drag
// events only (no DnD library dependency), matching the attached UX mockup.
const LOCKED_FIRST = "resume";
const LOCKED_LAST = "rank";
const PALETTE_KEYS = ["resume", "form", "mcq", "coding", "both", "ai", "rank"];

const STAGE_DESCRIPTIONS = {
  resume: "Score every resume against the job criteria",
  form: "Google Form for CTC, notice, logistics",
  mcq: "Timed multiple choice round",
  coding: "Run-and-submit coding problems",
  both: "MCQ and Coding back-to-back in one session",
  ai: "Adaptive voice interview, scored",
  rank: "Weighted overall score and rank"
};

const STAGE_META = {
  resume: "Auto ≥ configured minimum",
  form: "Optional — recruiter info only",
  mcq: "Reuses existing MCQ engine",
  coding: "Reuses existing Coding engine",
  both: "Reuses existing MCQ + Coding engines",
  ai: "Reuses existing AI Interview engine",
  rank: "Configurable weights"
};

export default function PipelineBuilder({ stages, onChange }) {
  const [dragKey, setDragKey] = useState(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);

  const insert = (key, atIndex) => {
    // MCQ / Coding / "both" are mutually exclusive assessment choices — adding
    // one swaps out the others rather than stacking them.
    const withoutAssessment = stages.filter((s) => s !== "mcq" && s !== "coding" && s !== "both" && s !== key);
    const target = ["mcq", "coding", "both"].includes(key) ? withoutAssessment : stages.filter((s) => s !== key);
    const index = atIndex == null ? Math.max(1, target.length - (target.includes(LOCKED_LAST) ? 1 : 0)) : atIndex;
    const next = [...target];
    next.splice(Math.min(index, next.length), 0, key);
    onChange(ensureLockedEnds(next));
  };

  const remove = (key) => {
    if (key === LOCKED_FIRST || key === LOCKED_LAST) return;
    onChange(stages.filter((s) => s !== key));
  };

  const reorder = (fromIndex, toIndex) => {
    if (stages[fromIndex] === LOCKED_FIRST || stages[fromIndex] === LOCKED_LAST) return;
    if (toIndex === 0 || toIndex >= stages.length) return; // never displace the locked ends
    const next = [...stages];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange(ensureLockedEnds(next));
  };

  return (
    <div className="pipeline-builder">
      <div className="pipeline-builder-palette">
        <span className="job-form-block-label">Features</span>
        <p className="rd-muted" style={{ margin: "2px 0 10px" }}>Drag a feature into the pipeline, or click to append.</p>
        {PALETTE_KEYS.map((key) => {
          const used = stages.includes(key);
          return (
            <div
              key={key}
              className={`pipeline-palette-item${used ? " used" : ""}`}
              draggable
              onDragStart={() => setDragKey(key)}
              onDragEnd={() => setDragKey(null)}
              onClick={() => insert(key, null)}
            >
              <span className="pipeline-drag-handle">⠿</span>
              <div className="pipeline-palette-copy">
                <strong>{PIPELINE_STAGE_LABELS[key]}</strong>
                <small>{STAGE_DESCRIPTIONS[key]}</small>
              </div>
              <span className={`pipeline-add-icon${used ? " added" : ""}`}>{used ? "✓" : "+"}</span>
            </div>
          );
        })}
      </div>

      <div
        className="pipeline-builder-canvas"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (dragKey && dragFromIndex == null) insert(dragKey, null); }}
      >
        <span className="job-form-block-label">Pipeline order</span>
        <p className="rd-muted" style={{ margin: "2px 0 14px" }}>{stages.length} stages — resume is always first, ranking is always last.</p>
        <div className="pipeline-builder-stages">
          {stages.map((key, index) => {
            const locked = key === LOCKED_FIRST || key === LOCKED_LAST;
            return (
              <div className="pipeline-stage-wrap" key={`${key}-${index}`}>
                <div
                  className="pipeline-stage-card"
                  draggable={!locked}
                  onDragStart={() => { setDragKey(key); setDragFromIndex(index); }}
                  onDragEnd={() => { setDragKey(null); setDragFromIndex(null); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFromIndex != null) reorder(dragFromIndex, index);
                    else if (dragKey) insert(dragKey, index);
                    setDragKey(null); setDragFromIndex(null);
                  }}
                >
                  <div className="pipeline-stage-top">
                    {!locked && <span className="pipeline-drag-handle">⠿</span>}
                    <span className="pipeline-stage-num">{String(index + 1).padStart(2, "0")}</span>
                    {!locked && (
                      <button type="button" className="pipeline-stage-remove" onClick={() => remove(key)} aria-label={`Remove ${PIPELINE_STAGE_LABELS[key]}`}>×</button>
                    )}
                  </div>
                  <strong>{PIPELINE_STAGE_LABELS[key]}</strong>
                  <small>{STAGE_META[key]}</small>
                  {locked && <span className="pipeline-stage-fixed">FIXED</span>}
                </div>
                {index < stages.length - 1 && <span className="pipeline-stage-arrow">→</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ensureLockedEnds(stages) {
  const withoutLocked = stages.filter((s) => s !== LOCKED_FIRST && s !== LOCKED_LAST);
  return [LOCKED_FIRST, ...withoutLocked, LOCKED_LAST];
}

export function defaultPipelineStages() {
  return [LOCKED_FIRST, "form", "both", "ai", LOCKED_LAST];
}
